import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { LangProvider } from "@/context/LangContext";
import { initAnalytics, trackEvent, trackContact } from "@/analytics";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { RateCompare } from "@/components/RateCompare";
import { Pricing } from "@/components/Pricing";
import { Campaign } from "@/components/Campaign";
import { Savings } from "@/components/Savings";
import { WhoFor } from "@/components/WhoFor";
import { UseCases } from "@/components/UseCases";
import { Why } from "@/components/Why";
import { ContactSection } from "@/components/ContactSection";
import { FinalCta } from "@/components/FinalCta";
import { Footer } from "@/components/Footer";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import LegalPage from "@/pages/LegalPage";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-navy-950 text-slate-400">
          Something went wrong. Please refresh the page.
        </div>
      );
    }
    return this.props.children;
  }
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent("page_view", { path: pathname });
  }, [pathname]);
  return null;
};

const Home = () => (
  <div className="relative min-h-screen overflow-x-clip">
    <Navbar />
    <main>
      <Hero />
      <RateCompare />
      <Pricing />
      <Campaign />
      <Savings />
      <WhoFor />
      <UseCases />
      <Why />
      <ContactSection />
      <FinalCta />
    </main>
    <Footer />
    <MobileStickyBar />
  </div>
);

function App() {
  useEffect(() => {
    initAnalytics();

    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    // centralized conversion tracking: WhatsApp / Telegram / Pricing CTAs
    // fires synchronously (capture phase) BEFORE the external navigation
    const onClick = (e) => {
      const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const tid = a.getAttribute("data-testid") || "";
      if (href.startsWith("https://wa.me")) {
        trackEvent("whatsapp_click", { testid: tid });
        trackContact("whatsapp");
      }
      if (href.startsWith("https://t.me")) {
        trackEvent("telegram_click", { testid: tid });
        trackContact("telegram");
      }
      if (href.startsWith("https://mbpayvia.com")) {
        const src = tid.includes("footer") ? "footer" : tid.includes("contact") ? "contact" : "navigation";
        trackEvent("official_website_click", { testid: tid, source_section: src });
      }
      if (tid.startsWith("pricing-tier-cta")) trackEvent("pricing_cta_click", { tier: tid });
    };
    document.addEventListener("click", onClick, true);

    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
      document.removeEventListener("click", onClick, true);
    };
  }, []);

  return (
    <LangProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/privacy" element={<LegalPage type="privacy" />} />
            <Route path="/terms" element={<LegalPage type="terms" />} />
            <Route path="/disclaimer" element={<LegalPage type="disclaimer" />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </LangProvider>
  );
}

export default App;
